import { useState, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import {
  IconCertificate2,
  IconSearch,
  IconLoader2,
  IconEye,
  IconRefresh,
  IconChevronLeft,
  IconChevronRight,
  IconChevronsLeft,
  IconChevronsRight,
} from "@tabler/icons-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { api } from "@/lib/api"
import { toast } from "sonner"

// ── Types ────────────────────────────────────────────────────────────

interface RecertifiableBenefitDto {
  benefitId: string
  recertifiedBenefitId: string | null
  applicationDate: string
  certificationDate: string
  intakeDate: string
  programType: number // 0 = PSE, 1 = LIHEAP
  applicantLastName: string
  applicantFirstName: string
  applicantSocialSecurityNumber: string
}

interface BenefitRecertified {
  originalBenefitId: string
  recertifiedBenefitId: string
  programType: number
}

// ── Helpers ──────────────────────────────────────────────────────────

const programLabels: Record<number, string> = {
  0: "PSE",
  1: "LIHEAP",
}

function getProgramLabel(type: number): string {
  return programLabels[type] ?? `Program ${type}`
}

function ProgramBadge({ type }: { type: number }) {
  return (
    <Badge variant={type === 0 ? "default" : "secondary"}>
      {getProgramLabel(type)}
    </Badge>
  )
}

function maskSsn(ssn: string): string {
  if (!ssn || ssn.length < 4) return ssn
  return `***-**-${ssn.slice(-4)}`
}

function formatDate(dateStr: string): string {
  if (!dateStr) return "—"
  const d = new Date(dateStr)
  return d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  })
}

const PAGE_SIZE_OPTIONS = [25, 50, 100]
const DEFAULT_PAGE_SIZE = 50

function Pagination({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: {
  currentPage: number
  totalPages: number
  totalItems: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
}) {
  if (totalItems <= PAGE_SIZE_OPTIONS[0]!) return null
  const start = (currentPage - 1) * pageSize + 1
  const end = Math.min(currentPage * pageSize, totalItems)

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t">
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <span>
          {start}–{end} of {totalItems.toLocaleString()}
        </span>
        <div className="flex items-center gap-1.5">
          <span>Rows:</span>
          <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
            <SelectTrigger className="h-7 w-[65px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((s) => (
                <SelectItem key={s} value={String(s)}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(1)}
        >
          <IconChevronsLeft className="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(currentPage - 1)}
        >
          <IconChevronLeft className="size-4" />
        </Button>
        <span className="px-2 text-sm tabular-nums">
          {currentPage} / {totalPages}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        >
          <IconChevronRight className="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(totalPages)}
        >
          <IconChevronsRight className="size-4" />
        </Button>
      </div>
    </div>
  )
}

// ── Page Component ───────────────────────────────────────────────────

export default function RecertificationPage() {
  const navigate = useNavigate()

  // Data state
  const [benefits, setBenefits] = useState<RecertifiableBenefitDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Search
  const [searchText, setSearchText] = useState("")

  // Pagination
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  // Recertify dialog
  const [confirmBenefit, setConfirmBenefit] = useState<RecertifiableBenefitDto | null>(null)
  const [recertifying, setRecertifying] = useState(false)

  // ── Load benefits ──────────────────────────────────────────────────

  const fetchBenefits = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await api.get<{ eligibleBenefits: RecertifiableBenefitDto[] }>(
        "/api/benefitapplications/recertifiable",
      )
      setBenefits(data.eligibleBenefits)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load recertifiable benefits")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchBenefits()
  }, [])

  // ── Client-side search filter (matches WinForms behavior) ─────────

  const filtered = useMemo(() => {
    if (!searchText.trim()) return benefits
    const term = searchText.trim().toLowerCase()
    const isDigits = /^\d+$/.test(term)

    return benefits.filter((b) => {
      if (isDigits) {
        // SSN search: match first N digits
        return b.applicantSocialSecurityNumber.startsWith(term)
      }
      // Name search: case-insensitive match on last or first name
      return (
        b.applicantLastName.toLowerCase().includes(term) ||
        b.applicantFirstName.toLowerCase().includes(term)
      )
    })
  }, [benefits, searchText])

  // ── Pagination ─────────────────────────────────────────────────────

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize)

  // Reset to page 1 when search changes
  useEffect(() => {
    setPage(1)
  }, [searchText])

  // ── Summary stats ──────────────────────────────────────────────────

  const stats = {
    total: benefits.length,
    eligible: benefits.filter((b) => !b.recertifiedBenefitId).length,
    recertified: benefits.filter((b) => !!b.recertifiedBenefitId).length,
    pse: benefits.filter((b) => b.programType === 0).length,
    liheap: benefits.filter((b) => b.programType === 1).length,
  }

  // ── Recertify action ──────────────────────────────────────────────

  const handleRecertify = async () => {
    if (!confirmBenefit) return
    setRecertifying(true)
    try {
      const result = await api.put<BenefitRecertified>(
        `/api/benefitapplications/${confirmBenefit.benefitId}/recertify`,
        {
          benefitId: confirmBenefit.benefitId,
          programType: confirmBenefit.programType,
        },
      )
      toast.success(
        `Benefit recertified successfully (${getProgramLabel(confirmBenefit.programType)})`,
      )
      setConfirmBenefit(null)
      navigate(`/benefit-applications/${result.recertifiedBenefitId}`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to recertify benefit")
    } finally {
      setRecertifying(false)
    }
  }

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <IconCertificate2 className="size-6" />
            <h1 className="text-2xl font-bold">Recertification</h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            View and recertify eligible benefit applications for the current program year.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchBenefits} disabled={loading}>
          <IconRefresh className={`size-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Summary cards */}
      {!loading && !error && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total Eligible</CardDescription>
              <CardTitle className="text-2xl tabular-nums">{stats.total}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Awaiting Recertification</CardDescription>
              <CardTitle className="text-2xl tabular-nums">{stats.eligible}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Already Recertified</CardDescription>
              <CardTitle className="text-2xl tabular-nums">{stats.recertified}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>By Program</CardDescription>
              <CardTitle className="text-2xl tabular-nums">
                <span className="text-base font-normal text-muted-foreground">PSE</span>{" "}
                {stats.pse}
                <span className="mx-2 text-muted-foreground/50">|</span>
                <span className="text-base font-normal text-muted-foreground">LIHEAP</span>{" "}
                {stats.liheap}
              </CardTitle>
            </CardHeader>
          </Card>
        </div>
      )}

      {/* Search bar */}
      <div className="flex items-center gap-2">
        <div className="relative max-w-sm flex-1">
          <IconSearch className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or SSN…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="pl-9"
          />
        </div>
        {searchText && (
          <span className="text-sm text-muted-foreground">
            {filtered.length} of {benefits.length} shown
          </span>
        )}
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-3 p-12 text-center">
              <p className="text-sm text-destructive">{error}</p>
              <Button variant="outline" size="sm" onClick={fetchBenefits}>
                Retry
              </Button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center gap-2 p-12 text-center">
              <IconCertificate2 className="size-10 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">
                {searchText
                  ? "No benefits match your search."
                  : "No benefits are currently eligible for recertification."}
              </p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Applicant</TableHead>
                    <TableHead>SSN</TableHead>
                    <TableHead>Program</TableHead>
                    <TableHead>Application Date</TableHead>
                    <TableHead>Certification Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginated.map((b) => {
                    const isRecertified = !!b.recertifiedBenefitId
                    return (
                      <TableRow key={b.benefitId}>
                        <TableCell className="font-medium">
                          {b.applicantLastName}, {b.applicantFirstName}
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {maskSsn(b.applicantSocialSecurityNumber)}
                        </TableCell>
                        <TableCell>
                          <ProgramBadge type={b.programType} />
                        </TableCell>
                        <TableCell>{formatDate(b.applicationDate)}</TableCell>
                        <TableCell>{formatDate(b.certificationDate)}</TableCell>
                        <TableCell>
                          {isRecertified ? (
                            <Badge variant="outline" className="text-green-600 border-green-300">
                              Recertified
                            </Badge>
                          ) : (
                            <Badge variant="secondary">Eligible</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {isRecertified ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                navigate(`/benefit-applications/${b.recertifiedBenefitId}`)
                              }
                            >
                              <IconEye className="size-4 mr-1.5" />
                              View
                            </Button>
                          ) : (
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => setConfirmBenefit(b)}
                            >
                              <IconCertificate2 className="size-4 mr-1.5" />
                              Recertify
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={filtered.length}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size)
                  setPage(1)
                }}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Confirmation dialog */}
      <AlertDialog open={!!confirmBenefit} onOpenChange={(open) => !open && setConfirmBenefit(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Recertification</AlertDialogTitle>
            <AlertDialogDescription>
              This will create a new {confirmBenefit && getProgramLabel(confirmBenefit.programType)}{" "}
              benefit application for{" "}
              <strong>
                {confirmBenefit?.applicantLastName}, {confirmBenefit?.applicantFirstName}
              </strong>{" "}
              based on the original certified benefit. Household members and income information will
              be copied; financial amounts will be reset to zero.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={recertifying}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRecertify} disabled={recertifying}>
              {recertifying && <IconLoader2 className="size-4 mr-1.5 animate-spin" />}
              Recertify
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
